import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

export type MultiSelectValue = string | number;

export interface MultiSelectOption {
  label: string;
  value: MultiSelectValue;
  disabled?: boolean;
}

export interface MultiSelectProps {
  /** Options to choose from */
  options: MultiSelectOption[];
  /** Currently selected values */
  selected: MultiSelectValue[];
  /** Called when the selection changes with the new array of selected values */
  onSelectedChanged: (selected: MultiSelectValue[]) => void;
  /** Custom function that produces the header text from the current selection */
  valueRenderer?: (
    selected: MultiSelectValue[],
    options: MultiSelectOption[],
  ) => React.ReactNode;
  /** Show a "Select All" toggle at the top of the list. Default true */
  hasSelectAll?: boolean;
  /** Hide the search input. Default false */
  disableSearch?: boolean;
  /** Disable the control */
  disabled?: boolean;
  /** Id applied to the dropdown trigger button */
  id?: string;
  /** Label used by the "Select All" option. Default "Select All" */
  selectAllLabel?: string;
  /** Width of the trigger and dropdown panel. Default 200 */
  width?: number | string;
  /** Override the search input placeholder. Default "Search" */
  searchPlaceholder?: string;
  /** Text shown in the header when nothing is selected and no valueRenderer is provided. Default "Select..." */
  emptyPlaceholder?: string;
}

const panelPadding = "var(--padding-10px)";

const headerSx = {
  width: "100%",
  height: 25,
  minHeight: 25,
  px: 1,
  py: "6px",
  bgcolor: "var(--page-header-button-bg-color)",
  boxShadow: "var(--md-box-shadow)",
  color: "var(--tertiary-color)",
  textTransform: "none" as const,
  "&:hover": {
    bgcolor: "var(--page-header-button-bg-color)",
    filter: "brightness(0.92)",
    boxShadow: "var(--md-box-shadow)", // otherwise lost on hover
  },
  "&.Mui-disabled": {
    opacity: 0.6,
    bgcolor: "var(--page-header-button-bg-color)",
    color: "var(--tertiary-color)",
  },
};

const checkboxSx = {
  color: "var(--page-header-button-bg-color)",
  p: "2px",
  "&.Mui-checked, &.MuiCheckbox-indeterminate": {
    color: "var(--page-header-button-bg-color)",
  },
};

const itemSx = {
  py: "2px",
  pl: 0,
  pr: 0,
  "&:hover": { bgcolor: "var(--hover-color)" },
};

const itemTextSx = {
  fontSize: "13px",
  whiteSpace: "nowrap" as const,
};

const listItemIconSx = { minWidth: 0, mr: 1, pl: panelPadding };

const labelSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  flex: 1,
  minWidth: 0,
  textAlign: "left",
};

const searchFieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "var(--grid-filter-input-bg-color)",
    fontSize: "13px",
    pl: 1,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--grid-filter-input-border-color)",
  },
  "& .MuiInputBase-input": { py: "6px", px: "4px" },
};

const MultiSelect: React.FC<MultiSelectProps> = (props) => {
  const {
    options,
    selected,
    onSelectedChanged,
    valueRenderer,
    hasSelectAll = true,
    disableSearch = false,
    disabled = false,
    id,
    selectAllLabel = "Select All",
    width = 200,
    searchPlaceholder = "Search",
    emptyPlaceholder = "Select...",
  } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [search, setSearch] = useState("");

  const isOpen = Boolean(anchorEl);

  const filteredOptions = useMemo(() => {
    const trimmed = search.trim().toLowerCase();
    if (!trimmed) return options;
    return options.filter((o) => o.label.toLowerCase().includes(trimmed));
  }, [options, search]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const allSelected = options.length > 0 && options.every((o) => selectedSet.has(o.value));
  const someSelected = !allSelected && selected.length > 0;

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
  };

  const handleToggle = (value: MultiSelectValue) => {
    if (selectedSet.has(value)) {
      onSelectedChanged(selected.filter((v) => v !== value));
    } else {
      onSelectedChanged([...selected, value]);
    }
  };

  const handleToggleAll = () => {
    if (allSelected) {
      onSelectedChanged([]);
    } else {
      onSelectedChanged(options.map((o) => o.value));
    }
  };

  const renderHeaderText = (): React.ReactNode => {
    if (valueRenderer) return valueRenderer(selected, options);
    if (selected.length === 0) return emptyPlaceholder;
    if (selected.length === options.length) return "All Selected";
    return `${selected.length} Selected`;
  };

  return (
    <Box sx={{ width, display: "inline-block" }}>
      <Button
        id={id}
        onClick={handleOpen}
        disabled={disabled}
        variant="contained"
        disableElevation
        disableRipple
        endIcon={<ArrowDropDownIcon />}
        sx={headerSx}
      >
        <Box component="span" sx={labelSx}>
          {renderHeaderText()}
        </Box>
      </Button>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        elevation={0}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              minWidth: width,
              width: "max-content",
              maxWidth: "min(90vw, 600px)",
              bgcolor: "var(--dropdown-bg-color)",
              borderRadius: "2px",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            maxHeight: "75vh",
            overflow: "hidden",
            pt: panelPadding,
            pb: panelPadding,
            gap: disableSearch ? 0 : panelPadding,
          }}
        >
          {!disableSearch && (
            <Box sx={{ px: panelPadding }}>
              <TextField
                autoFocus
                fullWidth
                size="small"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                variant="outlined"
                sx={searchFieldSx}
              />
            </Box>
          )}
          <List
            dense
            disablePadding
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              mx: 0,
              px: 0,
            }}
          >
            {hasSelectAll && (
              <ListItemButton onClick={handleToggleAll} sx={itemSx} disableGutters disableRipple>
                <ListItemIcon sx={listItemIconSx}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    tabIndex={-1}
                    disableRipple
                    sx={checkboxSx}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={<Typography sx={itemTextSx}>{selectAllLabel}</Typography>}
                />
              </ListItemButton>
            )}
            {filteredOptions.length === 0 && (
              <Box sx={{ py: 1, pl: panelPadding, pr: 0, textAlign: "center" }}>
                <Typography sx={{ color: "var(--disabled-font-color)", fontSize: "13px" }}>
                  No options
                </Typography>
              </Box>
            )}
            {filteredOptions.map((option) => {
              const isChecked = selectedSet.has(option.value);
              return (
                <ListItemButton
                  key={`${option.value}`}
                  onClick={() => handleToggle(option.value)}
                  disabled={option.disabled}
                  sx={itemSx}
                  disableGutters
                  disableRipple
                >
                  <ListItemIcon sx={listItemIconSx}>
                    <Checkbox
                      checked={isChecked}
                      tabIndex={-1}
                      disableRipple
                      sx={checkboxSx}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography sx={itemTextSx}>{option.label}</Typography>}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      </Popover>
    </Box>
  );
};

export default MultiSelect;
